import React from 'react';
import TopNavbar from './TopNavbar';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
import useSupplierProfilesStatus from '../../hooks/useSupplierProfilesStatus';
import useNotifications from '../../hooks/useNotifications';
import NotificationIcon from '../icons/NotificationIcon';
import OffcanvasMenu from './OffcanvasMenu'; // Assuming this component exists and handles the offcanvas menu structure
import DesktopSideMenu from './DesktopSideMenu'; // Assuming this component exists and handles the desktop sidebar structure
import Footer from './Footer'; // Assuming this component exists
import FormSpinnerOverlay from '../ui/FormSpinnerOverlay'; // Import loading spinner

import HomeIcon from '../icons/HomeIcon';
import RoutesIcon from '../icons/RoutesIcon';
import PaymentsIcon from '../icons/PaymentsIcon';
import CalendarIcon from '../icons/CalendarIcon';
import HelpIcon from '../icons/HelpIcon';
import SettingsIcon from '../icons/SettingsIcon'; // Reusing SettingsIcon for Configuración

// Import new icons from their modularized files
import UsersIcon from '../icons/UsersIcon';
import ContractsIcon from '../icons/ContractsIcon';
import EvaluationsIcon from '../icons/EvaluationsIcon';
import PaymentsAdminIcon from '../icons/PaymentsAdminIcon'; // Renamed to avoid conflict
import ChatIcon from '../icons/ChatIcon';
import TrainingIcon from '../icons/TrainingIcon';

// Menu items defined per role
const allRoleMenuItems = {
    user: [
        { text: "Inicio", href: "/home", icon: HomeIcon },
        { text: "Mis Rutas", href: "/itinerary", icon: RoutesIcon },
        { text: "Mis Pagos", href: "#", icon: PaymentsIcon },
        { text: "Agenda", href: "#", icon: CalendarIcon },
        { text: "Ayuda", href: "/faq", icon: HelpIcon }
    ],
    admin: [
        // These are the menu items currently defined for the admin role in this file
        { text: "Inicio", href: "/home", icon: HomeIcon }, // Assuming admin dashboard is at /admin/dashboard
        { text: "Usuarios", href: "/users", icon: UsersIcon },
        { text: "Contratos", href: "/contracts", icon: ContractsIcon },
        { text: "Evaluaciones", href: "/evaluations", icon: EvaluationsIcon },
        { text: "Estados de Pago", href: "/payments", icon: PaymentsAdminIcon },
        { text: "Chat", href: "/chat", icon: ChatIcon },
    ],
    supplier: [
        { text: "Inicio", href: "/home", icon: HomeIcon },
        { text: "Reservas", href: "/bookings", icon: CalendarIcon },
        { text: "Pagos", href: "/payments", icon: PaymentsIcon },
        { text: "Capacitación", href: "/training", icon: TrainingIcon },
        { text: "Ayuda", href: "/faq", icon: HelpIcon },
    ],
    agent: [
        { text: "Inicio", href: "/home", icon: HomeIcon },
        { text: "Solicitudes", href: "/requests", icon: ContractsIcon },
        { text: "Contratos", href: "/contracts", icon: ContractsIcon },
        { text: "Estados de Pago", href: "/payments", icon: PaymentsAdminIcon },
        { text: "Chat", href: "/chat", icon: ChatIcon },
    ],
    default: [ // Fallback menu if role is not matched or not available
        { text: "Inicio", href: "/home", icon: HomeIcon },
        { text: "Ayuda", href: "/faq", icon: HelpIcon },
    ]
};

const DashboardLayout = ({ children, activeMenuItem, isScrollable = false }) => {
    const { role, userId, getValidToken, isLoading: authLoading } = useAuth(); // Get the current user's role and id
    const { hasSelloCheckin, loadingProfiles } = useSupplierProfilesStatus();

    // Use notifications hook to determine if there are unread notifications
    const { notifications, loading: notificationsLoading } = useNotifications({
        userId,
        role,
        getValidToken,
        enabled: !!userId && !!role,
    });
    const hasUnread = notifications && notifications.some(n => !n.read);

    // Check if any data is still loading
    const isLoading = authLoading || loadingProfiles || notificationsLoading;

    // Select menu items based on role, fallback to default if role is undefined or not in allRoleMenuItems
    let mainMenuItems = allRoleMenuItems[role] || allRoleMenuItems.default;

    // For supplier, add "Servicios" if hasSelloCheckin is true
    if (role === "supplier" && hasSelloCheckin) {
        // Avoid duplicate "Servicios" if already present
        const alreadyHasServicios = mainMenuItems.some(item => item.href === "/services");
        if (!alreadyHasServicios) {
            mainMenuItems = [
                ...mainMenuItems.slice(0, 3), // Insert after Pagos
                { text: "Servicios", href: "/services", icon: SettingsIcon },
                ...mainMenuItems.slice(3)
            ];
        }
    }

    // IMPORTANT: Set this to the ACTUAL measured height of your TopNavbar (from browser dev tools, computed style)
    // plus a small safety margin. Navbar actual height: 84.09px.
    const navbarHeight = '85px'; // Adjusted for TopNavbar's actual height
    // This is the visual buffer BELOW the navbar when content is scrolled to the top of the content area.
    const contentScrollBuffer = '20px';

    // Style for the outermost div (red border)
    const layoutRootStyle = {
        paddingTop: navbarHeight,      // Space for the fixed TopNavbar
        display: 'flex',               // Make it a flex container
        flexDirection: 'column',       // Stack children vertically
        minHeight: '100vh',            // Ensure it takes at least full viewport height
        boxSizing: 'border-box',
    };

    // Style for the div that will contain the main content row (blue border)
    // This div will grow to fill available space between TopNavbar's padding and Footer
    const mainContentWrapperStyle = {
        flexGrow: 1,                   // Allow this container to grow
        display: 'flex',               // It will also be a flex container for its children (the row)
        overflow: 'hidden',            // Added to ensure it constrains its children's height properly
    };

    // Style for the Bootstrap .row inside mainContentWrapperStyle
    // The row will take height from its flex parent (mainContentWrapperStyle)
    // and the contentAreaStyle will handle internal scrolling.
    const rowStyle = {
        flexGrow: 1, // The row itself should grow to fill the mainContentWrapperStyle
    };

    // Style for the actual scrollable content area
    const contentAreaStyle = {
        overflowY: isScrollable ? 'auto' : 'visible',
        height: isScrollable ? '100%' : 'auto', // Take full height of the column it's in
        scrollPaddingTop: contentScrollBuffer,
    };

    const footerStyle = {
        flexShrink: 0 // Prevent footer from shrinking
    };

    return (
        <div style={{ ...layoutRootStyle }}>
            {/* Loading Spinner Overlay */}
            <FormSpinnerOverlay isLoading={isLoading} />

            {/* Top Navigation Bar */}
            <TopNavbar>
                {/* Notification icon in the navbar, right side */}
                <div style={{ position: 'absolute', right: 32, top: 24 }}>
                    <NotificationIcon active={hasUnread} />
                </div>
            </TopNavbar>

            {/* Show content only when not loading */}
            {!isLoading && (
                <>
                    {/* Offcanvas Menu (for mobile/tablet) */}
                    {/* This component renders the offcanvas HTML structure */}
                    <OffcanvasMenu menuItems={mainMenuItems} activeItemText={activeMenuItem} />

                    {/* Main content area wrapper - uses Bootstrap container-fluid */}
                    <div className="container-fluid" style={{ ...mainContentWrapperStyle }}>
                        {/* Bootstrap row to hold sidebar and main content */}
                        <div className="row" style={rowStyle}>

                            {/* Desktop Sidebar Menu */}
                            {/* This component renders the desktop sidebar HTML structure */}
                            <DesktopSideMenu
                                menuItems={mainMenuItems}
                                activeItemText={activeMenuItem}
                            />

                            {/* Main Content Area */}
                            {/* This is where the content of the specific screen (like HomeAdminScreen) is rendered */}
                            <div
                                className="col-md-9 ms-sm-auto col-lg-10 px-md-4 content-area"
                                style={contentAreaStyle}
                            >
                                {children} {/* The content of the current screen */}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Footer */}
            <Footer style={footerStyle} />
        </div>
    );
};

export default DashboardLayout;
